import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { WikiDocument } from 'src/lib/types';
import Checkbox from 'src/components/ui/Checkbox';
import { parseWikiDate } from './utils';

// Extended WikiDocument type with optional icon
export type WikiDocumentWithIcon = WikiDocument & { icon?: React.ReactNode };

// Selection column for multiselect
export const createSelectionColumn = (
  hoveredRow: string | number | null,
  isMobile: boolean
): ColumnDef<WikiDocumentWithIcon, any> => ({
  id: 'select',
  size: 48,
  minSize: 48,
  maxSize: 48,
  enableSorting: false,
  enableResizing: false,
  header: ({ table }) => (
    <div className="flex items-center justify-center">
      <Checkbox
        checked={table.getIsAllRowsSelected()}
        onChange={(e) => {
          e.stopPropagation();
          table.toggleAllRowsSelected();
        }}
        className="w-[48px] h-[48px] flex items-center justify-center"
      />
    </div>
  ),
  cell: ({ row, table }) => {
    const isSelected = row.getIsSelected();
    const rowId = row.id;
    const icon = row.original.icon;

    return (
      <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        {isSelected ? (
          <Checkbox
            checked={true}
            onChange={(e) => {
              e.stopPropagation();
              row.toggleSelected();
            }}
            className="w-[48px] h-[48px] flex items-center justify-center"
          />
        ) : isMobile || hoveredRow === rowId ? (
          <Checkbox
            checked={false}
            onChange={(e) => {
              e.stopPropagation();
              row.toggleSelected();
            }}
            className="w-[48px] h-[48px] flex items-center justify-center"
          />
        ) : (
          <div className="w-[48px] h-[48px] flex items-center justify-center">{icon}</div>
        )}
      </div>
    );
  }
});

// Icon-only column (when not using multiselect)
export const createIconColumn = (): ColumnDef<WikiDocumentWithIcon, any> => ({
  id: 'icon',
  size: 48,
  minSize: 48,
  maxSize: 48,
  enableSorting: false,
  enableResizing: false,
  header: () => <div className="w-[48px] h-[48px]"></div>,
  cell: ({ row }) => <div className="w-[48px] h-[48px] flex items-center justify-center">{row.original.icon}</div>
});

// Title column
// Note: This column is configured as "fluid" in DocumentBrowser (fluidColumnId="title"),
// meaning it automatically fills remaining space to prevent horizontal scrolling.
// The `size` value here (200) is only used as a base width if the user manually resizes it.
// Initially, the column width is undefined, allowing it to expand based on available space.
export const titleColumn: ColumnDef<WikiDocumentWithIcon, any> = {
  id: 'title',
  accessorFn: (row) => row.title,
  header: 'Title',
  size: 200, // Base size used after manual resize
  minSize: 200, // Minimum width constraint
  enableSorting: true,
  enableResizing: true,
  cell: ({ getValue }) => {
    const title = getValue() as string;
    return (
      <span className="truncate" title={title}>
        {title}
      </span>
    );
  }
};

// Category column
export const categoryColumn: ColumnDef<WikiDocumentWithIcon, any> = {
  id: 'category',
  accessorFn: (row) => row.tags?.category || '',
  header: 'Category',
  size: 150,
  minSize: 100,
  enableSorting: true,
  enableResizing: true,
  cell: ({ getValue }) => {
    const value = getValue() as string;
    return (
      <span className="truncate" title={value}>
        {value}
      </span>
    );
  }
};

// Domain column
export const domainColumn: ColumnDef<WikiDocumentWithIcon, any> = {
  id: 'domain',
  accessorFn: (row) => row.tags?.domain || '',
  header: 'Domain',
  size: 130,
  minSize: 100,
  enableSorting: true,
  enableResizing: true,
  cell: ({ getValue }) => {
    const value = getValue() as string;
    return (
      <span className="truncate" title={value}>
        {value}
      </span>
    );
  }
};

// SubDomain column
export const subDomainColumn: ColumnDef<WikiDocumentWithIcon, any> = {
  id: 'subDomain',
  accessorFn: (row) => row.tags?.subdomain || '',
  header: 'Sub Domain',
  size: 130,
  minSize: 100,
  enableSorting: true,
  enableResizing: true,
  cell: ({ getValue }) => {
    const value = getValue() as string;
    return (
      <span className="truncate" title={value}>
        {value}
      </span>
    );
  }
};

// Published column
export const publishedColumn: ColumnDef<WikiDocumentWithIcon, any> = {
  id: 'published',
  accessorFn: (row) => row.publication_date,
  header: 'Published',
  size: 120,
  minSize: 100,
  enableSorting: true,
  enableResizing: true,
  sortingFn: (rowA, rowB) => {
    const dateA = parseWikiDate(rowA.original.publication_date);
    const dateB = parseWikiDate(rowB.original.publication_date);
    const timeA = dateA?.getTime() || 0;
    const timeB = dateB?.getTime() || 0;
    return timeA - timeB;
  },
  cell: ({ getValue }) => {
    const dateObj = parseWikiDate(getValue() as string);
    const dateStr = dateObj ? dateObj.toLocaleDateString() : 'Unknown date';
    return (
      <span className="truncate" title={dateStr}>
        {dateStr}
      </span>
    );
  }
};

// Uploaded column (for Data Library)
export const uploadedColumn: ColumnDef<WikiDocumentWithIcon, any> = {
  id: 'uploaded',
  accessorFn: (row) => row.created_at,
  header: 'Uploaded',
  size: 120,
  minSize: 100,
  enableSorting: true,
  enableResizing: true,
  sortingFn: (rowA, rowB) => {
    const dateA = parseWikiDate(rowA.original.created_at);
    const dateB = parseWikiDate(rowB.original.created_at);
    const timeA = dateA?.getTime() || 0;
    const timeB = dateB?.getTime() || 0;
    return timeA - timeB;
  },
  cell: ({ getValue }) => {
    const dateObj = parseWikiDate(getValue() as string);
    const dateStr = dateObj ? dateObj.toLocaleDateString() : 'Unknown date';
    return (
      <span className="truncate" title={dateStr}>
        {dateStr}
      </span>
    );
  }
};

// Helper function to get columns based on configuration
export const getDocumentBrowserColumns = ({
  multiselect = false,
  hasIcons = false,
  hideCategoryColumn = false,
  showUploadedColumn = false,
  hoveredRow = null,
  isMobile = false
}: {
  multiselect?: boolean;
  hasIcons?: boolean;
  hideCategoryColumn?: boolean;
  showUploadedColumn?: boolean;
  hoveredRow?: string | number | null;
  isMobile?: boolean;
}): ColumnDef<WikiDocumentWithIcon, any>[] => {
  const columns: ColumnDef<WikiDocumentWithIcon, any>[] = [];

  // Add selection or icon column
  if (multiselect) {
    columns.push(createSelectionColumn(hoveredRow, isMobile));
  } else if (hasIcons) {
    columns.push(createIconColumn());
  }

  // Add data columns
  columns.push(titleColumn);
  if (!hideCategoryColumn) {
    columns.push(categoryColumn);
  }
  columns.push(domainColumn);
  columns.push(subDomainColumn);
  columns.push(publishedColumn);

  if (showUploadedColumn) {
    columns.push(uploadedColumn);
  }

  return columns;
};
